import { z } from 'zod';
import {
  BrandSchema,
  ProfilePresetSchema,
  ResolvedTenantProfileSchema,
  TenantProfileSchema,
  TermKeySchema,
} from './profiles';
import { UiLayoutSchema } from './ui-prefs';

// ── Organização (v11 §1): GET/PUT /org/settings — singleton por tenant ──────

/** limite do upload de logo (POST /org/settings/logo) */
export const ORG_LOGO_MAX_BYTES = 2 * 1024 * 1024; // 2MB

/** formatos de imagem aceitos para a logo */
export const ORG_LOGO_MIME_TYPES = ['image/png', 'image/jpeg', 'image/webp'] as const;
export type OrgLogoMimeType = (typeof ORG_LOGO_MIME_TYPES)[number];

/**
 * Wire do GET/PUT /org/settings. Find-or-default: sem linha salva, tudo null.
 * O fuso NÃO viaja: todo o produto assume America/Sao_Paulo (a UI mostra o
 * select desabilitado com nota). `logoUrl` é o CAMINHO da rota de serving
 * autenticada (`/org/settings/logo`, 302/modo JSON — padrão da mídia de chat),
 * nunca a chave crua do storage; null = sem logo.
 */
export const OrgSettingsSchema = z.object({
  name: z.string().nullable(),
  legalName: z.string().nullable(),
  cnpj: z.string().nullable(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  logoUrl: z.string().nullable(),
});
export type OrgSettings = z.infer<typeof OrgSettingsSchema>;

/**
 * PUT /org/settings — PARCIAL de propósito: a remoção da logo é um PUT só com
 * `{logoUrl: null}` (o upload tem POST próprio; um valor não-nulo aqui não faz
 * sentido e é rejeitado pelo z.null()). Campos ausentes preservam o valor.
 */
export const OrgSettingsPutBodySchema = z
  .object({
    name: z.string().trim().min(1).max(200),
    legalName: z.string().trim().max(200).nullable(),
    cnpj: z.string().trim().max(32).nullable(),
    phone: z.string().trim().max(40).nullable(),
    email: z.email().max(320).nullable(),
    /** só `null` (remover a logo) — subir logo é o POST multipart */
    logoUrl: z.null(),
  })
  .partial()
  .strict();
export type OrgSettingsPutBody = z.infer<typeof OrgSettingsPutBodySchema>;

// ── Perfil de operação (spec 017): GET/PUT /org/profile — admin ─────────────

/**
 * O que o admin edita: o perfil GRAVADO (preset + sobrescritas) e o layout
 * padrão do tenant — e, para a tela mostrar o que muda antes de salvar, o
 * resultado resolvido do que está gravado agora.
 */
export const OrgProfileSchema = z.object({
  profile: TenantProfileSchema,
  defaultLayout: UiLayoutSchema,
  resolved: ResolvedTenantProfileSchema,
});
export type OrgProfile = z.infer<typeof OrgProfileSchema>;

/** PUT /org/profile — substitui o perfil inteiro (termos omitidos voltam ao preset). */
export const OrgProfilePutBodySchema = z
  .object({
    profile: TenantProfileSchema,
    defaultLayout: UiLayoutSchema,
  })
  .strict();
export type OrgProfilePutBody = z.infer<typeof OrgProfilePutBodySchema>;

// ── Marca nas telas públicas (spec 017): GET /public/brand ──────────────────

/**
 * De qual tenant é a página pública: exatamente UM apontamento — o slug do
 * tenant (`/chat?tenant=`, `/kb?tenant=`) ou o token/slug que a própria página
 * já carrega (avaliação, formulário externo, artigo público, conversa do
 * widget). A página não precisa conhecer o tenant para mostrar a marca dele.
 */
export const PublicBrandQuerySchema = z
  .object({
    tenant: z.string().trim().min(1).max(100).optional(),
    rate: z.uuid().optional(),
    form: z.uuid().optional(),
    kb: z.uuid().optional(),
    chat: z.uuid().optional(),
  })
  .strict()
  .refine((q) => Object.values(q).filter((v) => v !== undefined).length === 1, {
    message: 'informe exatamente um de tenant, rate, form, kb ou chat',
  });
export type PublicBrandQuery = z.infer<typeof PublicBrandQuerySchema>;

/**
 * Perfil do tenant para quem não está logado: marca (logo por URL presignada
 * de curta duração) e vocabulário. Nada de recurso, naturezas nem layout.
 */
export const PublicTenantProfileSchema = z.object({
  preset: ProfilePresetSchema,
  terms: z.record(TermKeySchema, z.string()),
  brand: BrandSchema,
});
export type PublicTenantProfile = z.infer<typeof PublicTenantProfileSchema>;
